import React, { useState } from 'react';
import type { DeepAnalysisResult, DeepAnalysisSignal } from '../types';
import { CopyButton } from '@/components/CopyButton';
import { Sparkles, ChevronDown, ChevronRight } from 'lucide-react';

interface DeepAnalysisDisplayProps {
  analysis: DeepAnalysisResult;
}

const IMPACT_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  high: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', label: 'HIGH' },
  medium: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'MED' },
  low: { bg: 'bg-slate-500/15', text: 'text-slate-400', label: 'LOW' },
  none: { bg: 'bg-red-500/15', text: 'text-red-400', label: 'NONE' },
};

function SignalRow({ signal }: { signal: DeepAnalysisSignal }): React.ReactElement {
  const style = IMPACT_STYLES[signal.impact] || IMPACT_STYLES.low;

  return (
    <div className="flex items-start gap-3 py-1.5">
      <span className={`shrink-0 px-1.5 py-0.5 text-[9px] font-black uppercase rounded ${style.bg} ${style.text}`}>
        {style.label}
      </span>
      <div className="flex-1 min-w-0">
        <span className="text-[12px] font-mono text-slate-200">{signal.term}</span>
        <p className="text-[11px] text-slate-500 leading-snug">{signal.reason}</p>
        {signal.suggestion && (
          <p className="text-[11px] text-amber-400/80 leading-snug mt-0.5">
            Try: <span className="font-mono">{signal.suggestion}</span>
          </p>
        )}
      </div>
    </div>
  );
}

export function DeepAnalysisDisplay({ analysis }: DeepAnalysisDisplayProps): React.ReactElement {
  const [showSignals, setShowSignals] = useState(false);

  return (
    <div className="space-y-4 pt-3 border-t border-slate-700/50">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-purple-400" />
        <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Deep Analysis</span>
      </div>

      <p className="text-[12px] text-slate-300 leading-relaxed">{analysis.explanation}</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1.5">Suno Will Pick Up</p>
          <div className="space-y-1">
            {analysis.sunoWillPickUp.map((item, i) => (
              <p key={i} className="text-[11px] text-emerald-400/80 flex items-start gap-1.5">
                <span className="shrink-0">+</span> {item}
              </p>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-1.5">Suno Will Ignore</p>
          <div className="space-y-1">
            {analysis.sunoWillIgnore.map((item, i) => (
              <p key={i} className="text-[11px] text-red-400/80 flex items-start gap-1.5">
                <span className="shrink-0">-</span> {item}
              </p>
            ))}
          </div>
        </div>
      </div>

      {analysis.optimizedPrompt && (
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-3 relative group">
          <p className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1.5">Optimized Prompt</p>
          <p className="text-[12px] text-slate-200 font-mono leading-relaxed pr-8">{analysis.optimizedPrompt}</p>
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <CopyButton textToCopy={analysis.optimizedPrompt} />
          </div>
        </div>
      )}

      {analysis.termSignals.length > 0 && (
        <div>
          <button
            onClick={() => setShowSignals(!showSignals)}
            className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-400 transition-colors"
          >
            {showSignals ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            Term-by-Term ({analysis.termSignals.length})
          </button>
          {showSignals && (
            <div className="mt-2 space-y-0.5">
              {analysis.termSignals.map((signal, i) => <SignalRow key={i} signal={signal} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
